import React, { useState, useEffect } from 'react';
import { 
    Card, 
    List, 
    Typography, 
    Tag, 
    Button, 
    Space, 
    Rate, 
    Avatar, 
    Tooltip,
    Skeleton,
    Empty,
    Divider,
    Progress
} from 'antd';
import { 
    HeartOutlined, 
    StarOutlined, 
    EyeOutlined, 
    ShoppingCartOutlined,
    FireOutlined,
    CrownOutlined,
    ThunderboltOutlined,
    GiftOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const { Title, Text } = Typography;

const RecommendationEngine = ({ 
    type = 'vendors', // vendors, menus, packages
    limit = 6,
    showTitle = true,
    showReason = true,
    className = ''
}) => {
    const { user } = useAuth();
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchRecommendations();
    }, [type, limit, user]);

    const fetchRecommendations = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await api.getRecommendations({
                type,
                limit,
                user_id: user?.id
            });
            
            if (response.success) {
                setRecommendations(response.data);
            } else {
                setError(response.message || 'Failed to load recommendations');
            }
        } catch (err) {
            console.error('Failed to fetch recommendations:', err);
            setError('Failed to load recommendations');
        } finally {
            setLoading(false);
        }
    };

    const handleFavorite = async (item) => {
        try {
            const response = await api.toggleFavorite(
                item.type || type,
                item.id,
                item.category,
                item.metadata
            );
            
            if (response.success) {
                // Update local state
                setRecommendations(prev => 
                    prev.map(rec => 
                        rec.id === item.id 
                            ? { ...rec, is_favorited: response.data.action === 'added' }
                            : rec
                    )
                );
            }
        } catch (error) {
            console.error('Failed to toggle favorite:', error);
        }
    };

    const getRecommendationIcon = (reason) => {
        const icons = {
            'popular': <FireOutlined style={{ color: '#ff4d4f' }} />,
            'trending': <ThunderboltOutlined style={{ color: '#1890ff' }} />,
            'similar': <EyeOutlined style={{ color: '#52c41a' }} />,
            'personalized': <CrownOutlined style={{ color: '#faad14' }} />,
            'nearby': <GiftOutlined style={{ color: '#722ed1' }} />,
            'new': <StarOutlined style={{ color: '#13c2c2' }} />
        };
        return icons[reason] || <StarOutlined />;
    };

    const getRecommendationColor = (reason) => {
        const colors = {
            'popular': 'red',
            'trending': 'blue',
            'similar': 'green',
            'personalized': 'gold',
            'nearby': 'purple',
            'new': 'cyan'
        };
        return colors[reason] || 'default';
    };

    const renderVendorCard = (item) => (
        <Card
            key={item.id}
            hoverable
            className="recommendation-card"
            cover={
                <div className="relative">
                    <img 
                        alt={item.name}
                        src={item.image || '/images/placeholder-vendor.jpg'}
                        className="h-32 object-cover"
                    />
                    <div className="absolute top-2 right-2">
                        <Button
                            type="text"
                            icon={<HeartOutlined />}
                            className={`favorite-btn ${item.is_favorited ? 'favorited' : ''}`}
                            onClick={() => handleFavorite(item)}
                        />
                    </div>
                    {item.reason && showReason && (
                        <div className="absolute top-2 left-2">
                            <Tag 
                                color={getRecommendationColor(item.reason)}
                                icon={getRecommendationIcon(item.reason)}
                            >
                                {item.reason.charAt(0).toUpperCase() + item.reason.slice(1)}
                            </Tag>
                        </div>
                    )}
                </div>
            }
            actions={[
                <Button type="link" icon={<EyeOutlined />}>View</Button>,
                <Button type="link" icon={<ShoppingCartOutlined />}>Order</Button>
            ]}
        >
            <div className="space-y-2">
                <div className="flex justify-between items-start">
                    <Title level={5} className="mb-1">{item.name}</Title>
                    <Rate disabled value={item.rating} className="text-xs" />
                </div>
                
                <Text type="secondary" className="text-sm">
                    {item.cuisine} • {item.location}
                </Text>
                
                {item.price_range && (
                    <div className="flex items-center gap-1">
                        <Text className="text-sm font-medium">
                            {item.price_range}
                        </Text>
                        <Text type="secondary" className="text-xs">
                            per person
                        </Text>
                    </div>
                )}
                
                {item.confidence && (
                    <div className="space-y-1">
                        <div className="flex justify-between items-center">
                            <Text className="text-xs text-gray-500">Match</Text>
                            <Text className="text-xs font-medium">
                                {Math.round(item.confidence * 100)}%
                            </Text>
                        </div>
                        <Progress 
                            percent={Math.round(item.confidence * 100)} 
                            size="small" 
                            showInfo={false}
                            strokeColor="#1890ff"
                        />
                    </div>
                )}
            </div>
        </Card>
    );

    const renderMenuCard = (item) => (
        <Card
            key={item.id}
            hoverable
            className="recommendation-card"
            cover={
                <div className="relative">
                    <img 
                        alt={item.name}
                        src={item.image || '/images/placeholder-menu.jpg'}
                        className="h-24 object-cover"
                    />
                    <div className="absolute top-2 right-2">
                        <Button
                            type="text"
                            icon={<HeartOutlined />}
                            className={`favorite-btn ${item.is_favorited ? 'favorited' : ''}`}
                            onClick={() => handleFavorite(item)}
                        />
                    </div>
                </div>
            }
        >
            <div className="space-y-2">
                <div className="flex justify-between items-start">
                    <Title level={5} className="mb-1 text-sm">{item.name}</Title>
                    <Rate disabled value={item.rating} size="small" />
                </div>
                
                <Text type="secondary" className="text-xs">
                    {item.description}
                </Text>
                
                <div className="flex justify-between items-center">
                    <Text className="font-medium">₹{item.price}</Text>
                    <Button size="small" type="primary">Add to Cart</Button>
                </div>
            </div>
        </Card>
    );

    const renderPackageCard = (item) => (
        <Card
            key={item.id}
            hoverable
            className="recommendation-card"
        >
            <div className="space-y-3">
                <div className="flex justify-between items-start">
                    <div>
                        <Title level={5} className="mb-1">{item.name}</Title>
                        <Text type="secondary" className="text-sm">
                            {item.description}
                        </Text>
                    </div>
                    <Button
                        type="text"
                        icon={<HeartOutlined />}
                        className={`favorite-btn ${item.is_favorited ? 'favorited' : ''}`}
                        onClick={() => handleFavorite(item)}
                    />
                </div>
                
                <div className="space-y-2">
                    <div className="flex justify-between">
                        <Text className="font-medium">₹{item.price}</Text>
                        <Text type="secondary" className="text-sm">
                            {item.guest_count} guests
                        </Text>
                    </div>
                    
                    {item.features && (
                        <div className="flex flex-wrap gap-1">
                            {item.features.slice(0, 3).map((feature, index) => (
                                <Tag key={index} size="small">{feature}</Tag>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );

    const renderRecommendation = (item) => {
        switch (type) {
            case 'vendors':
                return renderVendorCard(item);
            case 'menus':
                return renderMenuCard(item);
            case 'packages':
                return renderPackageCard(item);
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className={className}>
                {showTitle && <Skeleton.Input active style={{ width: 200, marginBottom: 16 }} />}
                <div className="recommendations-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '16px',
                    justifyItems: 'center',
                    alignItems: 'end',
                    justifyContent: 'center',
                    alignContent: 'space-evenly'
                }}>
                    {Array.from({ length: limit }).map((_, index) => (
                        <Skeleton key={index} active />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={className}>
                {showTitle && <Title level={4}>Recommendations</Title>}
                <Empty 
                    description={error}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </div>
        );
    }

    if (recommendations.length === 0) {
        return (
            <div className={className}>
                {showTitle && <Title level={4}>Recommendations</Title>}
                <Empty 
                    description="No recommendations available"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </div>
        );
    }

    return (
        <div className={className}>
            {showTitle && (
                <div className="flex justify-between items-center mb-4">
                    <Title level={4}>
                        Recommended {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Title>
                    <Button type="link" onClick={fetchRecommendations}>
                        Refresh
                    </Button>
                </div>
            )}
            
            <div className="recommendations-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '16px',
                justifyItems: 'center',
                alignItems: 'end',
                justifyContent: 'center',
                alignContent: 'space-evenly'
            }}>
                {recommendations.map(renderRecommendation)}
            </div>
        </div>
    );
};

export default RecommendationEngine;
